/**
 * Impression navigateur — étiquettes Code 128 (A4 grille ou thermique 80×40 mm).
 * JsBarcode local uniquement ; aucune requête CDN.
 */

import JsBarcode from 'jsbarcode';

export const BARCODE_PRINT_OPTIONS = {
  format: 'CODE128',
  displayValue: false,
  margin: 6,
  width: 1.6,
  height: 48,
};

const CLKI_CODE_REGEX = /^CLKI[0-9A-HJKMNP-Z]{8}$/;

/** Échappement HTML strict pour texte utilisateur (nom produit, variante, prix affiché). */
export function escapeHtmlText(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Génère le markup SVG Code 128 (document DOM requis).
 * @param {Document} doc
 */
export function generateCode128SvgMarkup(doc, code, opts = BARCODE_PRINT_OPTIONS) {
  const normalized = String(code ?? '').trim().toUpperCase();
  if (!CLKI_CODE_REGEX.test(normalized)) {
    throw new Error('Code interne invalide pour impression');
  }
  const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, normalized, opts);
  const markup = new XMLSerializer().serializeToString(svg);
  if (!markup || !/<(rect|path|g)\b/i.test(markup)) {
    throw new Error('Génération Code 128 vide');
  }
  return markup;
}

function labelBlockHtml({ title, variantLabel, priceLabel, code, format, barcodeSvgMarkup }) {
  const isThermal = format === 'thermal';
  const priceBlock = priceLabel
    ? `<div class="price">${escapeHtmlText(priceLabel)}</div>`
    : '';
  const variantBlock = variantLabel
    ? `<div class="variant">${escapeHtmlText(variantLabel)}</div>`
    : '';
  return `
    <div class="label ${isThermal ? 'thermal' : 'a4'}">
      <div class="title">${escapeHtmlText(title)}</div>
      ${variantBlock}
      ${priceBlock}
      <div class="barcode-wrap">${barcodeSvgMarkup}</div>
      <div class="code">${escapeHtmlText(code)}</div>
    </div>
  `;
}

/**
 * Prépare le HTML imprimable (testable sans window.open).
 * @returns {{ ok: true, html: string } | { ok: false, error: string }}
 */
export function buildInternalBarcodePrintDocument({ labels, format = 'a4', doc = typeof document !== 'undefined' ? document : null }) {
  if (!doc) {
    return { ok: false, error: 'Environnement navigateur requis pour l’impression.' };
  }
  if (!Array.isArray(labels) || labels.length === 0) {
    return { ok: false, error: 'Aucune étiquette à imprimer.' };
  }

  const blocks = [];
  for (const label of labels) {
    try {
      const barcodeSvgMarkup = generateCode128SvgMarkup(doc, label.code);
      blocks.push(
        labelBlockHtml({
          ...label,
          format,
          barcodeSvgMarkup,
        })
      );
    } catch (err) {
      return {
        ok: false,
        error: `Échec du code-barres « ${label.code} » : ${err?.message || 'erreur inconnue'}`,
      };
    }
  }

  const thermalCss =
    format === 'thermal'
      ? `.sheet{display:flex;flex-direction:column;gap:0}.label.thermal{width:80mm;height:40mm;padding:3mm;page-break-after:always;box-sizing:border-box}`
      : `.sheet{display:grid;grid-template-columns:repeat(3,1fr);gap:8mm;padding:10mm}.label.a4{border:1px dashed #ccc;padding:6mm;box-sizing:border-box;min-height:42mm}`;

  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Étiquettes</title>
    <style>
      body{font-family:system-ui,sans-serif;margin:0}
      ${thermalCss}
      .title{font-weight:600;font-size:11pt;line-height:1.2;margin-bottom:2mm}
      .variant{font-size:9pt;color:#444;margin-bottom:2mm}
      .price{font-size:10pt;margin-bottom:2mm}
      .code{font-family:monospace;text-align:center;font-size:9pt;margin-top:2mm;letter-spacing:0.05em}
      .barcode-wrap svg.barcode{width:100%;max-height:18mm;display:block;margin:0 auto}
      @media print{.no-print{display:none}}
    </style>
  </head><body>
    <div class="no-print" style="padding:8px"><button type="button" id="print-btn">Imprimer</button></div>
    <div class="sheet">${blocks.join('')}</div>
    <script>
      document.getElementById('print-btn').addEventListener('click', function(){ window.print(); });
    <\/script>
  </body></html>`;

  return { ok: true, html };
}

/**
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function openInternalBarcodePrintWindow({ labels, format = 'a4' }) {
  const built = buildInternalBarcodePrintDocument({ labels, format });
  if (!built.ok) {
    return built;
  }

  const w = window.open('', '_blank', 'noopener,noreferrer');
  if (!w) {
    return { ok: false, error: 'Impossible d’ouvrir la fenêtre d’impression (bloqueur de pop-ups ?).' };
  }

  w.document.open();
  w.document.write(built.html);
  w.document.close();
  return { ok: true };
}
